import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { FiMic, FiMicOff, FiVideo, FiVideoOff, FiX } from "react-icons/fi";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "../context/TranslationContext";
import usePrefersReducedMotion from "./motion/usePrefersReducedMotion";

/**
 * Choose camera/mic before joining LiveKit as host or stage speaker.
 */
export default function LiveStreamDeviceModal({
  isOpen,
  onClose,
  onConfirm,
  isDark,
  variant = "host",
}) {
  const { t } = useTranslation();
  const reduced = usePrefersReducedMotion();
  const [camera, setCamera] = useState(false);
  const [microphone, setMicrophone] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setCamera(false);
      setMicrophone(false);
    }
  }, [isOpen, variant]);

  const baseText = "text-label-primary";
  const mutedText = "text-label-secondary";
  const cardBase = "border-separator-default bg-bg-raised";

  const title =
    variant === "stage"
      ? t("liveStream.devicePromptStageTitle") || "Join as a speaker"
      : t("liveStream.devicePromptHostTitle") || "Before you go live";

  const subtitle =
    variant === "stage"
      ? t("liveStream.devicePromptStageHint") ||
        "Choose whether to turn on your camera and microphone."
      : t("liveStream.devicePromptHostHint") ||
        "Choose your camera and microphone settings. Nothing is shared until you enable it here or in the stream controls.";

  const toggleClass = (on) =>
    `flex flex-1 items-center justify-center gap-2 rounded-radius-sm border px-3 py-3 text-subheadline-semibold transition ${
      on
        ? isDark
          ? "border-accent-default bg-accent-tinted text-accent-text"
          : "border-status-success bg-status-success-bg text-status-success"
        : isDark
          ? "border-white/10 bg-white/[0.03] text-slate-300 hover:bg-white/[0.06]"
          : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
    }`;

  const handleConfirm = () => {
    onConfirm?.({ camera, microphone });
  };

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {isOpen ? (
        <motion.div
          className="fixed inset-0 z-[9999] flex items-end justify-center bg-overlay-scrim min-[600px]:items-center min-[600px]:p-4"
          initial={reduced ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={reduced ? { opacity: 0 } : { opacity: 0 }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) onClose?.();
          }}
        >
          <motion.div
            role="dialog"
            aria-modal="true"
            className={`w-full max-w-[480px] rounded-t-radius-xl border shadow-overlay min-[600px]:rounded-radius-lg ${cardBase}`}
            initial={reduced ? false : { opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={reduced ? { opacity: 0 } : { opacity: 0, y: 8, scale: 0.98 }}
          >
            <div
              className={`flex items-start justify-between border-b px-5 py-4 ${
                "border-separator-default"
              }`}
            >
              <div>
                <h2 className={`text-headline ${baseText}`}>{title}</h2>
                <p className={`mt-1 text-footnote leading-relaxed ${mutedText}`}>{subtitle}</p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className={`rounded-radius-xs p-2 hover:bg-fill-quaternary`}
                aria-label={t("common.close") || "Close"}
              >
                <FiX className={`h-5 w-5 ${mutedText}`} />
              </button>
            </div>

            <div className="space-y-3 px-5 py-5">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setCamera((v) => !v)}
                  className={toggleClass(camera)}
                >
                  {camera ? (
                    <FiVideo className="h-4 w-4 shrink-0" />
                  ) : (
                    <FiVideoOff className="h-4 w-4 shrink-0" />
                  )}
                  {camera
                    ? t("liveStream.cameraOn") || "Camera on"
                    : t("liveStream.cameraOff") || "Camera off"}
                </button>
                <button
                  type="button"
                  onClick={() => setMicrophone((v) => !v)}
                  className={toggleClass(microphone)}
                >
                  {microphone ? (
                    <FiMic className="h-4 w-4 shrink-0" />
                  ) : (
                    <FiMicOff className="h-4 w-4 shrink-0" />
                  )}
                  {microphone
                    ? t("liveStream.micOn") || "Mic on"
                    : t("liveStream.micOff") || "Mic off"}
                </button>
              </div>

              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={onClose}
                  className={`rounded-radius-sm border px-4 py-2 text-footnote-semibold ${
                    "border-separator-default text-label-secondary hover:bg-fill-quaternary"
                  }`}
                >
                  {t("common.cancel") || "Cancel"}
                </button>
                <button
                  type="button"
                  onClick={handleConfirm}
                  className="rounded-radius-sm bg-app-live-base px-4 py-2 text-footnote-semibold text-app-live-on hover:brightness-110"
                >
                  {t("liveStream.devicePromptContinue") || "Continue"}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>,
    document.body
  );
}
